import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, WorkoutSession, TrainingDayExercise, Exercise, ExercisePerformance } from '../database';
import { applyProgressionToTrainingDay } from '../utils/progression';

const WorkoutSessionPage: React.FC = () => {
  const { sessionId, exerciseIndex } = useParams();
  const navigate = useNavigate();

  // Add CSS for hiding scrollbars
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .exercise-carousel::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Determine if we're in edit mode based on URL path
  const isEditMode = window.location.pathname.includes('/workout-edit/');
  const editExerciseIndex = exerciseIndex ? parseInt(exerciseIndex) : 0;

  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [exercises, setExercises] = useState<(TrainingDayExercise & { exercise: Exercise })[]>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [performances, setPerformances] = useState<ExercisePerformance[]>([]);
  
  // Store data for each exercise independently
  const [exerciseData, setExerciseData] = useState<{[key: number]: {
    finalReps: number;
    notes: string;
    discoveryWeight: number;
    previousPerformance: ExercisePerformance | null;
    previousSessionDate: Date | null;
    currentSessionPerformance: ExercisePerformance | null;
  }}>({});

  const loadWorkoutSession = useCallback(async () => {
    try {
      const sessionData = await db.workoutSessions.get(parseInt(sessionId!));
      if (!sessionData) {
        navigate('/');
        return;
      }

      setSession(sessionData);

      // Load existing performances for this session
      const existingPerformances = await db.exercisePerformances
        .where('sessionId')
        .equals(sessionData.id!)
        .toArray();
      setPerformances(existingPerformances);

      if (isEditMode && existingPerformances.length > 0) {
        // In edit mode, build exercises from performance data to show actual session weights
        const exercisesWithDetails = await Promise.all(
          existingPerformances
            .sort((a, b) => a.order - b.order)
            .map(async (perf) => {
              const exercise = await db.exercises.get(perf.exerciseId);
              return {
                id: perf.exerciseId,
                trainingDayId: sessionData.trainingDayId,
                exerciseId: perf.exerciseId,
                weight: perf.weight, // Use the actual weight from the session
                sets: perf.targetSets || [],
                order: perf.order,
                discoveryMode: false, // Edit mode shows completed exercises, not discovery
                exercise: exercise!
              };
            })
        );
        setExercises(exercisesWithDetails);
      } else {
        // Normal workout mode - load from training day template
        const trainingDayExercises = await db.trainingDayExercises
          .where('trainingDayId')
          .equals(sessionData.trainingDayId)
          .sortBy('order');

        // Load exercise details
        const exercisesWithDetails = await Promise.all(
          trainingDayExercises.map(async (tde) => {
            const exercise = await db.exercises.get(tde.exerciseId);
            return { ...tde, exercise: exercise! };
          })
        );
        setExercises(exercisesWithDetails);

        // Determine the correct exercise position for continuing sessions
        if (sessionData.currentExerciseIndex !== undefined) {
          // Use saved position if available
          setCurrentExerciseIndex(sessionData.currentExerciseIndex);
        } else if (existingPerformances.length > 0) {
          // Fall back to finding next uncompleted exercise
          const nextExerciseIndex = exercisesWithDetails.findIndex((exercise, index) => {
            const hasPerformance = existingPerformances.some(perf => 
              perf.exerciseId === exercise.exerciseId && perf.order === index
            );
            return !hasPerformance;
          });
          
          const targetIndex = nextExerciseIndex === -1 ? exercisesWithDetails.length - 1 : nextExerciseIndex;
          setCurrentExerciseIndex(targetIndex);
        }
      }

      // Set exercise index for edit mode
      if (isEditMode) {
        setCurrentExerciseIndex(editExerciseIndex);
      }

    } catch (error) {
      console.error('Failed to load workout session:', error);
    }
  }, [sessionId, navigate, isEditMode, editExerciseIndex]);

  const loadAllExerciseData = useCallback(async () => {
    if (exercises.length === 0) return;

    const newExerciseData: {[key: number]: {
      finalReps: number;
      notes: string;
      discoveryWeight: number;
      previousPerformance: ExercisePerformance | null;
      previousSessionDate: Date | null;
      currentSessionPerformance: ExercisePerformance | null;
    }} = {};

    // Load data for each exercise
    for (let index = 0; index < exercises.length; index++) {
      const exercise = exercises[index];
      
      try {
        // Load previous performance for this exercise
        let query = db.exercisePerformances
          .where('exerciseId')
          .equals(exercise.exerciseId);
        
        if (isEditMode) {
          query = query.filter(perf => perf.sessionId !== session?.id);
        }
        
        const previousPerf = await query.reverse().first();
        let previousSessionDate = null;
        
        if (previousPerf) {
          const previousSession = await db.workoutSessions.get(previousPerf.sessionId);
          previousSessionDate = previousSession?.startTime || null;
        }

        // Load current performance for this exercise
        const existingPerf = performances.find(p => p.exerciseId === exercise.exerciseId);
        
        let finalReps = 0;
        let notes = '';
        let discoveryWeight = 0;
        let currentSessionPerformance = null;

        if (existingPerf) {
          finalReps = existingPerf.finalSetActualReps;
          notes = existingPerf.notes || '';
          
          if (isEditMode) {
            currentSessionPerformance = existingPerf;
          }
        } else if (!isEditMode) {
          finalReps = exercise.sets[exercise.sets.length - 1]?.targetReps || 0;
          
          if (exercise.discoveryMode) {
            const existingPerfForWeight = performances.find(p => p.exerciseId === exercise.exerciseId);
            discoveryWeight = existingPerfForWeight?.weight || 0;
          }
        }

        newExerciseData[index] = {
          finalReps,
          notes,
          discoveryWeight,
          previousPerformance: previousPerf || null,
          previousSessionDate,
          currentSessionPerformance
        };
      } catch (error) {
        console.error(`Failed to load data for exercise ${index}:`, error);
        // Set default values on error
        newExerciseData[index] = {
          finalReps: exercise.sets[exercise.sets.length - 1]?.targetReps || 0,
          notes: '',
          discoveryWeight: 0,
          previousPerformance: null,
          previousSessionDate: null,
          currentSessionPerformance: null
        };
      }
    }

    setExerciseData(newExerciseData);
  }, [exercises, performances, isEditMode, session?.id]);

  const getExerciseData = useCallback((exerciseIndex: number) => {
    return exerciseData[exerciseIndex] || {
      finalReps: 0,
      notes: '',
      discoveryWeight: 0,
      previousPerformance: null,
      previousSessionDate: null,
      currentSessionPerformance: null
    };
  }, [exerciseData]);

  // Auto-save function for immediate database persistence
  const autoSaveExerciseData = useCallback(async (exerciseIndex: number, field: string, value: any) => {
    try {
      const currentExercise = exercises[exerciseIndex];
      if (!currentExercise || !session?.id) return;

      // Update React state immediately for UI responsiveness
      setExerciseData(prev => ({
        ...prev,
        [exerciseIndex]: {
          ...prev[exerciseIndex],
          [field]: value
        }
      }));

      // Clear any existing save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounced database save (500ms delay)
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const exerciseData = getExerciseData(exerciseIndex);
          const updatedData = { ...exerciseData, [field]: value };

          // Check if this exercise already has a performance record for this session
          let existingPerformance = await db.exercisePerformances
            .where('sessionId')
            .equals(session.id!)
            .and(perf => perf.exerciseId === currentExercise.exerciseId)
            .first();

          const performanceData = {
            sessionId: session.id!,
            exerciseId: currentExercise.exerciseId,
            weight: currentExercise.discoveryMode ? updatedData.discoveryWeight : currentExercise.weight,
            targetSets: currentExercise.sets,
            finalSetActualReps: updatedData.finalReps,
            notes: updatedData.notes.trim() || undefined,
            order: exerciseIndex,
            skipped: false
          };

          if (existingPerformance) {
            // Update existing performance
            await db.exercisePerformances.update(existingPerformance.id!, performanceData);
          } else {
            // Create new performance record
            await db.exercisePerformances.add(performanceData);
          }

          console.log(`Auto-saved ${field} for exercise ${exerciseIndex}:`, value);
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }, 500);
    } catch (error) {
      console.error('Auto-save setup failed:', error);
    }
  }, [exercises, session?.id, getExerciseData]);

  // Helper function to update exercise data with auto-save
  const updateExerciseData = useCallback((exerciseIndex: number, field: string, value: any) => {
    autoSaveExerciseData(exerciseIndex, field, value);
  }, [autoSaveExerciseData]);

  useEffect(() => {
    if (sessionId) {
      loadWorkoutSession();
    }
  }, [sessionId, loadWorkoutSession]);

  useEffect(() => {
    if (exercises.length > 0) {
      loadAllExerciseData();
    }
  }, [exercises, performances, loadAllExerciseData]);

  // Simple scroll end detection - works well with CSS scroll snap
  useEffect(() => {
    if (!carouselRef.current || exercises.length === 0) return;

    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      // Clear previous timeout
      clearTimeout(scrollTimeout);
      
      // Wait for scroll to end, then detect position
      scrollTimeout = setTimeout(() => {
        if (!carouselRef.current) return;
        
        const carousel = carouselRef.current;
        const scrollLeft = carousel.scrollLeft;
        const cardWidth = carousel.scrollWidth / exercises.length;
        const newIndex = Math.round(scrollLeft / cardWidth);
        
        if (newIndex !== currentExerciseIndex && newIndex >= 0 && newIndex < exercises.length) {
          setCurrentExerciseIndex(newIndex);
          
          // Debounce database save
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          
          saveTimeoutRef.current = setTimeout(() => {
            if (session?.id) {
              db.workoutSessions.update(session.id, { 
                currentExerciseIndex: newIndex 
              }).catch(error => {
                console.error('Failed to update current exercise index:', error);
              });
            }
          }, 300);
        }
      }, 100); // Good balance of responsiveness and smooth scrolling
    };

    const carousel = carouselRef.current;
    carousel.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      carousel.removeEventListener('scroll', handleScroll);
    };
  }, [exercises.length, currentExerciseIndex, session?.id]);


  // Initial scroll to current exercise when exercises load
  useEffect(() => {
    if (carouselRef.current && exercises.length > 0) {
      const carousel = carouselRef.current;
      const cardWidth = carousel.scrollWidth / exercises.length;
      const targetScrollLeft = cardWidth * currentExerciseIndex;
      
      // Use setTimeout to ensure the DOM is ready
      setTimeout(() => {
        carousel.scrollLeft = targetScrollLeft;
      }, 100);
    }
  }, [exercises.length, currentExerciseIndex]); // Run when exercises load or currentIndex changes

  const saveCurrentExercise = async (skipExercise: boolean = false) => {
    try {
      const currentExercise = exercises[currentExerciseIndex];
      if (!currentExercise) return;

      const currentData = exerciseData[currentExerciseIndex];
      if (!currentData) return;

      const performanceData: Omit<ExercisePerformance, 'id'> = {
        sessionId: session!.id!,
        exerciseId: currentExercise.exerciseId,
        weight: currentExercise.discoveryMode ? currentData.discoveryWeight : currentExercise.weight,
        targetSets: currentExercise.sets,
        finalSetActualReps: skipExercise ? 0 : currentData.finalReps,
        skipped: skipExercise,
        notes: currentData.notes.trim() || undefined,
        order: currentExerciseIndex
      };

      // Check if performance already exists for this exercise in this session (check database directly)
      const existingPerf = await db.exercisePerformances
        .where('sessionId')
        .equals(session!.id!)
        .and(perf => perf.exerciseId === currentExercise.exerciseId)
        .first();
      
      if (existingPerf) {
        await db.exercisePerformances.update(existingPerf.id!, performanceData);
        // Update state
        setPerformances(performances.map(p => 
          p.id === existingPerf.id ? { ...existingPerf, ...performanceData } : p
        ));
      } else {
        const perfId = await db.exercisePerformances.add(performanceData);
        setPerformances([...performances, { id: perfId, ...performanceData }]);
      }

      // Handle discovery mode completion - save universal weight and disable discovery
      if (currentExercise.discoveryMode && !skipExercise && currentData.discoveryWeight > 0) {
        try {
          // Update the exercise's universal weight
          await db.exercises.update(currentExercise.exerciseId, {
            universalWeight: currentData.discoveryWeight,
            universalWeightType: '5RM' as const, // Assuming 5RM for now
            universalWeightUpdatedAt: new Date(),
            updatedAt: new Date()
          });

          // Update the training day exercise to disable discovery mode and set the weight
          await db.trainingDayExercises.update(currentExercise.id!, {
            discoveryMode: false,
            weight: currentData.discoveryWeight
          });

          // Update local state to reflect the change
          setExercises(exercises.map((ex, idx) => 
            idx === currentExerciseIndex 
              ? { ...ex, discoveryMode: false, weight: currentData.discoveryWeight }
              : ex
          ));
        } catch (error) {
          console.error('Failed to update discovery mode settings:', error);
        }
      }

    } catch (error) {
      console.error('Failed to save exercise performance:', error);
    }
  };

  const nextExercise = () => {
    if (isEditMode) {
      // In edit mode, return to summary without saving (auto-save handles data)
      navigate(`/summary/${sessionId}`);
    } else {
      // Normal workout flow - pure navigation
      if (currentExerciseIndex < exercises.length - 1) {
        const nextIndex = currentExerciseIndex + 1;
        setCurrentExerciseIndex(nextIndex);
        
        // Scroll to next card
        if (carouselRef.current) {
          const carousel = carouselRef.current;
          const cardWidth = carousel.scrollWidth / exercises.length;
          const targetScrollLeft = cardWidth * nextIndex;
          
          carousel.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        }
        
        // Update database with new position only
        if (session?.id) {
          db.workoutSessions.update(session.id, { 
            currentExerciseIndex: nextIndex 
          }).catch(error => {
            console.error('Failed to update current exercise index:', error);
          });
        }
      } else {
        completeWorkout();
      }
    }
  };


  const skipExercise = async () => {
    await saveCurrentExercise(true);
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    } else {
      completeWorkout();
    }
  };

  const completeWorkout = async () => {
    try {
      const endTime = new Date();
      const duration = session ? Math.floor((endTime.getTime() - session.startTime.getTime()) / 1000 / 60) : 0;

      // Explicitly save the current exercise with completion logic
      await saveCurrentExercise();

      // Mark session as completed
      await db.workoutSessions.update(session!.id!, {
        endTime,
        duration,
        completed: true
      });

      // Get all performances for progression calculation
      const allPerformances = await db.exercisePerformances
        .where('sessionId')
        .equals(session!.id!)
        .toArray();

      // Apply progression logic to update weights for next time
      await applyProgressionToTrainingDay(session!.trainingDayId, allPerformances);

      // Update routine to next training day
      const routine = await db.routines.get(session!.routineId);
      if (routine) {
        const nextDayIndex = (routine.currentDayIndex + 1) % routine.trainingDayIds.length;
        await db.routines.update(routine.id!, { currentDayIndex: nextDayIndex });
      }

      navigate(`/summary/${session!.id}`);
    } catch (error) {
      console.error('Failed to complete workout:', error);
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!session || exercises.length === 0) {
    return (
      <div className="text-center py-8">
        <p>Loading workout...</p>
      </div>
    );
  }


  return (
    <div className="h-full overflow-hidden -m-4">
      {/* Styled horizontal carousel */}
      <div 
        ref={carouselRef}
        className="exercise-carousel flex h-full overflow-x-auto snap-x snap-mandatory"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {exercises.map((exercise, index) => {
          const data = getExerciseData(index);
          
          return (
            <div 
              key={index} 
              className="flex-shrink-0 snap-center px-4 py-4 flex items-center justify-center" 
              style={{ width: '100vw' }}
            >
              {/* Compact card */}
              <div className="bg-white shadow-lg rounded-xl p-4 w-full max-w-sm border border-gray-200 flex flex-col h-fit max-h-full overflow-hidden">
              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-1 leading-tight">
                  {exercise.exercise.name}
                </h2>
                {exercise.exercise.description && (
                  <p className="text-gray-600 text-xs">
                    {exercise.exercise.description}
                  </p>
                )}
                
                {/* Muscle Groups */}
                <div className="flex flex-wrap gap-1 mt-2 justify-center">
                  {exercise.exercise.primaryMuscles?.slice(0, 2).map(muscle => (
                    <span key={muscle} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                      {muscle}
                    </span>
                  ))}
                  {exercise.exercise.primaryMuscles && exercise.exercise.primaryMuscles.length > 2 && (
                    <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                      +{exercise.exercise.primaryMuscles.length - 2}
                    </span>
                  )}
                </div>
              </div>

              {/* Weight and Sets - Compact Layout */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-3 mb-4 text-white">
                {exercise.discoveryMode ? (
                  <div className="text-center">
                    <div className="text-xs font-medium mb-2 opacity-90">Discovery Mode</div>
                    <div className="flex items-center justify-center space-x-2">
                      <input
                        type="number"
                        value={data.discoveryWeight || ''}
                        onChange={(e) => updateExerciseData(index, 'discoveryWeight', parseFloat(e.target.value) || 0)}
                        className="w-20 p-2 text-xl font-bold text-center text-gray-900 border border-white/20 rounded bg-white/90 focus:ring-1 focus:ring-yellow-400"
                        placeholder="0"
                        step="0.5"
                        min="0"
                      />
                      <span className="text-xl font-bold">lbs</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-3xl font-bold">
                      {exercise.weight}
                      <span className="text-base ml-1 opacity-90">lbs</span>
                    </div>
                  </div>
                )}
                
                {/* Sets Display */}
                <div className="flex justify-center items-center gap-4 mt-3">
                  {exercise.sets.map((set, setIndex) => (
                    <div key={setIndex} className="text-center">
                      <div className="text-xs opacity-75 mb-1">Set {setIndex + 1}</div>
                      <div className="text-base font-bold bg-white/20 rounded px-2 py-1">
                        {set.targetReps}
                      </div>
                    </div>
                  ))}
                </div>
              </div>


              {/* Previous Performance */}
              {data.previousPerformance && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                  <div className="text-xs font-medium text-amber-800 mb-1">
                    Last Time{data.previousSessionDate && ` (${formatDate(data.previousSessionDate)})`}
                  </div>
                  <div className="flex justify-between text-sm text-amber-700">
                    <span className="font-semibold">{data.previousPerformance.weight} lbs</span>
                    <span>Final: {data.previousPerformance.finalSetActualReps} reps</span>
                  </div>
                  {data.previousPerformance.notes && (
                    <div className="text-xs text-amber-600 mt-1 italic truncate" title={data.previousPerformance.notes}>
                      "{data.previousPerformance.notes}"
                    </div>
                  )}
                </div>
              )}

              {/* Edit Mode Performance Display */}
              {isEditMode && data.currentSessionPerformance && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                  <h4 className="font-medium text-blue-800 mb-1 text-sm">Original Session</h4>
                  <div className="text-xs text-blue-700 space-y-1">
                    <p><span className="font-medium">Weight:</span> {data.currentSessionPerformance.weight} lbs</p>
                    <p><span className="font-medium">Final set:</span> {data.currentSessionPerformance.finalSetActualReps} reps</p>
                    {data.currentSessionPerformance.notes && (
                      <p><span className="font-medium">Notes:</span> {data.currentSessionPerformance.notes}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Input Section */}
              <div className="space-y-4 flex-1">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Final Set Actual Reps
                  </label>
                  <input
                    type="number"
                    value={data.finalReps || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '0') {
                        updateExerciseData(index, 'finalReps', 0);
                      } else {
                        updateExerciseData(index, 'finalReps', parseInt(value) || 0);
                      }
                    }}
                    className="w-full p-4 text-2xl font-bold text-center border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    min="0"
                    max="50"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <input
                    type="text"
                    value={data.notes}
                    onChange={(e) => updateExerciseData(index, 'notes', e.target.value)}
                    className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Quick notes..."
                  />
                </div>

                {/* Primary Action Button - Always show appropriate button */}
                {!isEditMode && (
                  <div className="pt-2">
                    {index === exercises.length - 1 ? (
                      <button
                        onClick={nextExercise}
                        className={`w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-lg shadow-lg hover:from-green-700 hover:to-emerald-700 transition-all ${
                          index !== currentExerciseIndex ? 'pointer-events-none' : ''
                        }`}
                      >
                        Complete Workout
                      </button>
                    ) : (
                      <button
                        onClick={nextExercise}
                        className={`w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-lg shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all ${
                          index !== currentExerciseIndex ? 'pointer-events-none' : ''
                        }`}
                      >
                        Next Exercise
                      </button>
                    )}
                  </div>
                )}

                {/* Skip Link - Always show for consistent sizing */}
                {!isEditMode && (
                  <div className="text-center">
                    <button
                      onClick={skipExercise}
                      className={`text-sm text-gray-500 hover:text-gray-700 transition-colors ${
                        index !== currentExerciseIndex ? 'pointer-events-none' : ''
                      }`}
                    >
                      Skip this exercise
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Progress dots */}
      <div className="fixed bottom-24 left-0 right-0 z-30 pointer-events-none">
        <div className="flex justify-center items-center gap-2">
          {exercises.map((_, index) => (
            <div
              key={index}
              className={`rounded-full transition-all duration-300 ${
                index === currentExerciseIndex
                  ? 'w-8 h-3 bg-blue-500 shadow-lg'
                  : index < currentExerciseIndex
                  ? 'w-3 h-3 bg-green-500 shadow-md'
                  : 'w-3 h-3 bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkoutSessionPage;